/**
 * The figurative Newsmonger-UI mockups shown on the Appearance theme picker
 * (FR-3.74, NM-25).
 *
 * Each tile is a tiny "app window": a header bar with the watch-dial accent, a
 * sidebar of topic rows, and a couple of story cards in the feed. It is a
 * *figure* of the real UI, not a literal copy — enough that the eye reads
 * "Newsmonger, in this palette" at ~110px wide.
 *
 * ## Why the colours are literal hex, never the CSS tokens
 *
 * A theme tile has to show **its own** theme no matter what the app is currently
 * wearing — the Dark tile is dark even while the app is light, so a reader can
 * see what they are choosing before they choose it. Binding the fills to
 * `var(--paper)` &c. would repaint every tile in the *active* theme and collapse
 * all three into the same picture. So the palettes below are frozen copies of
 * the light and dark token values (`styles.scss`); keep them in step by hand if
 * those ever move — `tests/unit/theme-tiles.test.ts` pins the pairing.
 *
 * `Auto` is the same mockup split down a diagonal: light on the left, dark on
 * the right, the way macOS draws its Auto appearance tile. The two halves are
 * the very same `mockup()` clipped to opposite sides of the frame.
 */

import type { SafeHtml } from 'kerfjs';

export type ThemeTileKind = 'auto' | 'light' | 'dark';

/** The four inks a tile is painted with — one frozen set per theme. */
interface TilePalette {
  /** Paper / card fill. */
  bg: string;
  /** Header bar and sidebar blocks. */
  soft: string;
  /** Text bars (used at low opacity for the thin lines). */
  text: string;
  /** The pine accent: the dial, the selected topic, a card's headline. */
  accent: string;
}

/** Frozen copies of the light token values (`:root` in styles.scss). */
export const LIGHT_TILE: TilePalette = { bg: '#fbfcfb', soft: '#d8e7e2', text: '#1a2420', accent: '#17604f' };
/** Frozen copies of the dark token values (`@mixin dark-tokens` in styles.scss). */
export const DARK_TILE: TilePalette = { bg: '#161d1a', soft: '#1c332c', text: '#e4e8e6', accent: '#4da88e' };

/**
 * The window mockup itself, palette-driven so the same shapes render in any
 * theme. Coordinates are in the shared 132×92 viewBox; the caller clips it to a
 * rounded frame (and, for Auto, to one side of a diagonal).
 */
function mockup(p: TilePalette): SafeHtml {
  return (
    <g>
      {/* paper */}
      <rect x="0" y="0" width="132" height="92" fill={p.bg} />
      {/* header bar, the dial accent, and a title bar beside it */}
      <rect x="0" y="0" width="132" height="17" fill={p.soft} />
      <circle cx="11" cy="8.5" r="4.5" fill={p.accent} />
      <rect x="22" y="6.5" width="46" height="4" rx="2" fill={p.text} opacity="0.5" />
      {/* sidebar with topic rows — one selected, in the accent */}
      <rect x="0" y="17" width="46" height="75" fill={p.soft} />
      <rect x="9" y="28" width="28" height="6" rx="3" fill={p.text} opacity="0.45" />
      <rect x="9" y="41" width="28" height="6" rx="3" fill={p.accent} />
      <rect x="9" y="54" width="28" height="6" rx="3" fill={p.text} opacity="0.28" />
      <rect x="9" y="67" width="28" height="6" rx="3" fill={p.text} opacity="0.28" />
      {/* story card 1: a bold title over two thin text lines */}
      <rect x="56" y="26" width="66" height="27" rx="5" fill={p.bg} stroke={p.text} strokeOpacity="0.14" />
      <rect x="62" y="32" width="42" height="5" rx="2.5" fill={p.text} opacity="0.72" />
      <rect x="62" y="41" width="52" height="3" rx="1.5" fill={p.text} opacity="0.26" />
      <rect x="62" y="47" width="44" height="3" rx="1.5" fill={p.text} opacity="0.26" />
      {/* story card 2: an accent headline, so the accent reads twice */}
      <rect x="56" y="58" width="66" height="27" rx="5" fill={p.bg} stroke={p.text} strokeOpacity="0.14" />
      <rect x="62" y="64" width="34" height="5" rx="2.5" fill={p.accent} />
      <rect x="62" y="73" width="52" height="3" rx="1.5" fill={p.text} opacity="0.26" />
      <rect x="62" y="79" width="46" height="3" rx="1.5" fill={p.text} opacity="0.26" />
    </g>
  );
}

/**
 * One theme tile's SVG. Decorative (`aria-hidden`) — the accessible name comes
 * from the radio's own `<label>` text beside it.
 *
 * Clip ids are suffixed with `kind` so all three tiles can share the document
 * without colliding; the Auto tile adds a left/right pair for its diagonal
 * split. Nested `clip-path`s intersect, so each half is clipped to *both* the
 * rounded frame and its side of the diagonal.
 */
export function themeTileSvg(kind: ThemeTileKind): SafeHtml {
  const clip = `theme-tile-clip-${kind}`;
  return (
    <svg class="theme-tile-svg" viewBox="0 0 132 92" role="img" aria-hidden="true">
      <defs>
        <clipPath id={clip}>
          <rect x="0" y="0" width="132" height="92" rx="9" />
        </clipPath>
        {kind === 'auto' ? (
          <g>
            <clipPath id={`${clip}-left`}>
              <polygon points="0,0 76,0 52,92 0,92" />
            </clipPath>
            <clipPath id={`${clip}-right`}>
              <polygon points="76,0 132,0 132,92 52,92" />
            </clipPath>
          </g>
        ) : (
          ''
        )}
      </defs>
      {kind === 'auto' ? (
        <g clipPath={`url(#${clip})`}>
          <g clipPath={`url(#${clip}-left)`}>{mockup(LIGHT_TILE)}</g>
          <g clipPath={`url(#${clip}-right)`}>{mockup(DARK_TILE)}</g>
        </g>
      ) : (
        <g clipPath={`url(#${clip})`}>{mockup(kind === 'dark' ? DARK_TILE : LIGHT_TILE)}</g>
      )}
    </svg>
  );
}
